import {
  areCoordinatesWithinShapeBounds,
  hasPositivePolygonRingArea
} from "helpers/shape-coordinate-validation";

/**
 * 解析上传的 GeoJSON 文件
 * @param file - 要解析的文件
 * @param onParsedSuccess - 成功回调
 * @param onError - 错误回调
 */

export interface ParseGeoJsonResult {
  category: "polyline" | "polygon";
  feature: any;
  fileName: string;
}

export interface ParseGeoJsonError {
  title: string;
  message: string;
}

const GEOJSON_EXT_REGEXP = /\.geojson$/i;
const POLAR_LATITUDE_EPSILON = 1e-9;

const isGeoJsonExtension = (fileName: string) =>
  GEOJSON_EXT_REGEXP.test(fileName);

const isPolarLatitude = (latitude: number) =>
  Math.abs(Math.abs(latitude) - 90) <= POLAR_LATITUDE_EPSILON;

const isValidPoint = (point: any) => {
  return (
    Array.isArray(point) &&
    point.length >= 2 &&
    Number.isFinite(point[0]) &&
    Number.isFinite(point[1])
  );
};

const hasMinimumLinePoints = (coordinates: any) => {
  return Array.isArray(coordinates) && coordinates.length >= 2;
};

const isValidLineCoordinateFormat = (coordinates: any) => {
  return (
    Array.isArray(coordinates) &&
    coordinates.every((point) => isValidPoint(point))
  );
};

const isClosedRing = (ring: any[]) => {
  if (!Array.isArray(ring) || ring.length < 4) {
    return false;
  }

  const first = ring[0];
  const last = ring[ring.length - 1];
  if (!isValidPoint(first) || !isValidPoint(last)) {
    return false;
  }

  return first[0] === last[0] && first[1] === last[1];
};

const hasMinimumDistinctPoints = (ring: any[]): boolean => {
  if (!Array.isArray(ring) || ring.length < 3) {
    return false;
  }

  const distinctPoints = new Set<string>();
  for (let i = 0; i < ring.length; i++) {
    if (!isValidPoint(ring[i])) {
      return false;
    }
    const [x, y] = ring[i];
    distinctPoints.add(`${x},${y}`);
  }

  return distinctPoints.size >= 3;
};

const isValidPolygonCoordinateFormat = (coordinates: any) => {
  if (!Array.isArray(coordinates) || coordinates.length < 1) {
    return false;
  }

  return coordinates.every(
    (ring: any) =>
      Array.isArray(ring) && ring.every((point: any) => isValidPoint(point))
  );
};

const getOuterRing = (coordinates: any): any[] | undefined => {
  if (!Array.isArray(coordinates) || coordinates.length < 1) {
    return undefined;
  }

  const [outerRing] = coordinates;
  if (!Array.isArray(outerRing)) {
    return undefined;
  }

  return outerRing;
};

const getAllPoints = (geometryType: string, coordinates: any): any[] => {
  if (geometryType === "LineString") {
    return Array.isArray(coordinates) ? coordinates : [];
  }

  if (geometryType === "Polygon") {
    if (!Array.isArray(coordinates)) {
      return [];
    }

    return coordinates.flat();
  }

  return [];
};

const areLongitudesWithinBounds = (points: any[]) => {
  return points.every(
    (point) =>
      isValidPoint(point) &&
      (isPolarLatitude(point[1]) || (point[0] >= -180 && point[0] <= 180))
  );
};

const areLatitudesWithinBounds = (points: any[]) => {
  return points.every(
    (point) => isValidPoint(point) && point[1] >= -90 && point[1] <= 90
  );
};

const getFileNameWithoutExt = (fileName: string) => {
  return fileName.replace(/\.geojson$/i, "").trim() || fileName;
};

const buildGeometryError = (geometryType: any): ParseGeoJsonError => {
  if (geometryType === "Polygon") {
    return {
      title: "polygonInvalidCoordinatesTitle",
      message: "polygonInvalidCoordinatesMessage"
    };
  }

  return {
    title: "unsupportedGeometryTitle",
    message: "unsupportedGeometryMessage"
  };
};

const hasOwnField = (value: any, fieldName: string) => {
  return !!value && Object.prototype.hasOwnProperty.call(value, fieldName);
};

const validateFeatureGeometry = (
  feature: any,
  onError: (error: ParseGeoJsonError) => void
) => {
  if (!hasOwnField(feature, "geometry") || !feature?.geometry) {
    onError({
      title: "geoJsonMissingGeometryTitle",
      message: "geoJsonMissingGeometryMessage"
    });
    return false;
  }

  const geometryType = feature?.geometry?.type;
  const coordinates = feature?.geometry?.coordinates;

  if (!hasOwnField(feature.geometry, "coordinates")) {
    onError({
      title: "geoJsonMissingCoordinatesTitle",
      message: "geoJsonMissingCoordinatesMessage"
    });
    return false;
  }

  if (geometryType === "polygon") {
    onError({
      title: "geoJsonInvalidGeometryTypeTitle",
      message: "geoJsonInvalidGeometryTypeMessage"
    });
    return false;
  }

  // 5. geometry.type 仅支持 LineString / Polygon
  if (geometryType !== "LineString" && geometryType !== "Polygon") {
    onError({
      title: "unsupportedGeometryTitle",
      message: "unsupportedGeometryMessage"
    });
    return false;
  }

  // 6. 坐标格式必须为数值点
  if (
    (geometryType === "LineString" &&
      !isValidLineCoordinateFormat(coordinates)) ||
    (geometryType === "Polygon" && !isValidPolygonCoordinateFormat(coordinates))
  ) {
    onError(buildGeometryError(geometryType));
    return false;
  }

  const points = getAllPoints(geometryType, coordinates);

  // 7. 经度范围 [-180, 180]
  if (!areLongitudesWithinBounds(points)) {
    onError({
      title: "invalidCoordinateBoundsTitle",
      message: "invalidCoordinateBoundsMessage"
    });
    return false;
  }

  // 8. 纬度范围 [-90, 90]
  if (!areLatitudesWithinBounds(points)) {
    onError({
      title: "invalidCoordinateBoundsTitle",
      message: "invalidCoordinateBoundsMessage"
    });
    return false;
  }

  // 9. 线段点数
  if (geometryType === "LineString" && !hasMinimumLinePoints(coordinates)) {
    onError(buildGeometryError(geometryType));
    return false;
  }

  if (geometryType === "Polygon") {
    const outerRing = getOuterRing(coordinates);
    if (!outerRing) {
      onError({
        title: "polygonInvalidCoordinatesTitle",
        message: "polygonInvalidCoordinatesMessage"
      });
      return false;
    }

    // 10. 区域点数（至少3个有效点）
    if (!hasMinimumDistinctPoints(outerRing)) {
      onError({
        title: "polygonAreaZeroImportTitle",
        message: "polygonAreaZeroImportMessage"
      });
      return false;
    }

    // 11. 区域闭合
    if (!isClosedRing(outerRing)) {
      onError({
        title: "polygonNotClosedTitle",
        message: "polygonNotClosedMessage"
      });
      return false;
    }

    // 12. 区域面积（使用实际面积判定，避免极小合法面被浮点阈值误杀）
    if (!hasPositivePolygonRingArea(outerRing)) {
      onError({
        title: "polygonAreaZeroImportTitle",
        message: "polygonAreaZeroImportMessage"
      });
      return false;
    }

    // 13. 区域内洞（仅支持单外环）
    if (coordinates.length > 1) {
      onError({
        title: "polygonHoleTitle",
        message: "polygonHoleMessage"
      });
      return false;
    }
  }

  if (!areCoordinatesWithinShapeBounds(coordinates)) {
    onError({
      title: "invalidCoordinateBoundsTitle",
      message: "invalidCoordinateBoundsMessage"
    });
    return false;
  }

  return true;
};

export function parseGeoJsonFile(
  file: File,
  onParsedSuccess: (data: ParseGeoJsonResult) => void,
  onError: (error: ParseGeoJsonError) => void
) {
  // 1. 文件后缀必须是 .geojson
  if (!isGeoJsonExtension(file.name)) {
    onError({
      title: "geoJsonExtensionInvalidTitle",
      message: "geoJsonExtensionInvalidMessage"
    });
    return;
  }

  const reader = new FileReader();

  reader.onloadend = (e) => {
    try {
      const text = new TextDecoder().decode(
        new Uint8Array(e.target?.result as ArrayBuffer)
      );
      // 2. JSON 格式
      const geoJson = JSON.parse(text);

      const fileName = getFileNameWithoutExt(file.name);

      if (!hasOwnField(geoJson, "type")) {
        onError({
          title: "geoJsonMissingTypeTitle",
          message: "geoJsonMissingTypeMessage"
        });
        return;
      }

      const isFeatureCollection = geoJson?.type === "FeatureCollection";
      const isSingleFeature = geoJson?.type === "Feature";

      if (!isFeatureCollection && !isSingleFeature) {
        onError({
          title: "geoJsonStructureInvalidTitle",
          message: "geoJsonStructureInvalidMessage"
        });
        return;
      }

      // 3. GeoJSON 结构要求为标准 GeoJSON 顶层结构：Feature 或 FeatureCollection

      // 4. features 必须是数组且不能为空
      if (
        isFeatureCollection &&
        (!Array.isArray(geoJson.features) || geoJson.features.length === 0)
      ) {
        onError({
          title: "geoJsonFeaturesInvalidTitle",
          message: "geoJsonFeaturesInvalidMessage"
        });
        return;
      }

      const features = isFeatureCollection ? geoJson.features : [geoJson];

      for (const feature of features) {
        if (!validateFeatureGeometry(feature, onError)) {
          return;
        }
      }

      // 14. 不支持一次导入多个形状
      if (features.length > 1) {
        onError({
          title: "polygonSingleOnlyTitle",
          message: "polygonSingleOnlyMessage"
        });
        return;
      }

      const feature = features[0];
      const geometryType = feature?.geometry?.type;

      onParsedSuccess({
        category: geometryType === "Polygon" ? "polygon" : "polyline",
        feature,
        fileName
      });
    } catch {
      onError({
        title: "unsupportedGeometryTitle",
        message: "unsupportedGeometryMessage"
      });
    }
  };

  reader.readAsArrayBuffer(file);
}
